import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Package, Truck, Plane, FileUp, Globe, ArrowRight } from "lucide-react";
import BookingFlow from "@/components/BookingFlow";
import BulkCsv from "@/components/BulkCsv";
import IntlBooking from "@/components/IntlBooking";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { k: "b2c", label: "B2C Parcel", icon: Package },
  { k: "b2b", label: "B2B Freight", icon: Truck },
  { k: "air", label: "Air Express", icon: Plane },
  { k: "intl", label: "International", icon: Globe },
  { k: "bulk", label: "Bulk CSV (B2C)", icon: FileUp },
];

export default function AddOrder() {
  const [params] = useSearchParams();
  const initial = TABS.some((t) => t.k === params.get("tab")) ? params.get("tab") : "b2c";
  const [tab, setTab] = useState(initial);
  const { user } = useAuth();
  const navigate = useNavigate();
  // Contract-rate accounts book at their rate card (same as the app) — bulk CSV stays available
  const rateCardUser = user?.rate_card_enabled === true;

  return (
    <div data-testid="add-order-page" className="space-y-6 max-w-6xl">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Add Order</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Book a shipment</h1>
        <p className="text-sm text-zinc-500 mt-2">
          B2C, B2B freight, Air express, International to 220+ countries — or bulk B2C via CSV.
        </p>
      </div>

      <div className="flex gap-0 border-2 border-obsidian w-fit bg-white flex-wrap" data-testid="add-order-tabs">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`add-order-tab-${t.k}`}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${tab === t.k ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {rateCardUser && tab !== "bulk" ? (
        <div className="border-2 border-obsidian bg-white p-8 max-w-2xl" data-testid="add-order-ratecard-notice">
          <p className="font-bold text-lg">Your account is on contracted rate-card pricing.</p>
          <p className="text-sm text-zinc-500 mt-2">
            Single bookings are made at your negotiated card rates — exactly like the app. Bulk CSV upload remains available below.
          </p>
          <button onClick={() => navigate("/dashboard/ratecard")} data-testid="add-order-goto-ratecard"
            className="mt-5 inline-flex items-center gap-2 bg-acid text-white font-bold px-6 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150">
            Book at my card rates <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : tab === "bulk" ? (
        <BulkCsv />
      ) : tab === "intl" ? (
        <IntlBooking />
      ) : (
        <div>
          {tab === "b2c" && (
            <button onClick={() => setTab("bulk")} data-testid="b2c-bulk-link"
              className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border-2 border-obsidian px-4 py-2.5 bg-white hover:bg-acid hover:text-white transition-colors duration-150">
              <FileUp className="w-3.5 h-3.5" /> Many orders? Bulk CSV upload →
            </button>
          )}
          <BookingFlow key={tab} fixedMode={tab} />
        </div>
      )}
    </div>
  );
}
